import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * BannerSlider - Pure React slider dùng ảnh trong /public
 *
 * props:
 * - images: string[]     // đường dẫn tuyệt đối: "/images/xxx.jpg"
 * - interval: number     // ms, thời gian chuyển slide
 * - height: string       // lớp tailwind cho chiều cao khung (responsive)
 * - showArrows: boolean
 * - showDots: boolean
 * - overlay: boolean     // phủ gradient để chữ / search nổi hơn
 * - children: ReactNode  // nội dung phủ trên banner (ví dụ ô tìm kiếm)
 */
export default function BannerSlider({
  images = ["/images/banner-1.jpg", "/images/banner-2.jpg", "/images/banner-3.jpg", "/images/banner-4.jpg"],
  interval = 5000,
  height = "h-[260px] md:h-[360px] lg:h-[460px]",
  showArrows = true,
  showDots = true,
  overlay = true,
  children,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  // Tự động chạy
  useEffect(() => {
    if (paused || images.length <= 1) return;
    timerRef.current = setInterval(next, interval);
    return () => clearInterval(timerRef.current);
  }, [paused, images.length, interval, next]);

  // Điều khiển bằng phím ← →
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  return (
    <section
      className={`relative w-full ${height} overflow-hidden select-none`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      {/* Các ảnh chồng lên nhau, fade bằng opacity */}
      {images.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={`Banner ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === index ? "opacity-100" : "opacity-0"
            }`}
          onError={(e) => {
            // fallback nếu ảnh lỗi
            e.currentTarget.src = "/images/banner-fallback.jpg";
          }}
          draggable={false}
        />
      ))}

      {/* Lớp phủ gradient cho nội dung nổi hơn */}
      {overlay && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/40" />
      )}

      {/* Nội dung phủ lên banner (ví dụ ô tìm kiếm) */}
      {children && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl">{children}</div>
        </div>
      )}

      {/* Mũi tên */}
      {showArrows && images.length > 1 && (
        <>
          <button
            aria-label="Ảnh trước"
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/35 hover:bg-black/50 text-white p-2"
            onClick={prev}
          >
            {/* chevron left */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            aria-label="Ảnh sau"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/35 hover:bg-black/50 text-white p-2"
            onClick={next}
          >
            {/* chevron right */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}

      {/* Chấm điều hướng */}
      {showDots && images.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              aria-label={`Chuyển tới ảnh ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-2.5 rounded-full transition-all ${i === index ? "w-6 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"
                }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
